import { AVAILS, LENGTHS, TYPES } from '@blisspointmedia/bpm-types/dist/LinearBuying';

/**
 * Blanket instructions are an ICON local-linear feature: ICON stations are the ones that accept a
 * blanket set of instructions, so the editor only ever offers ICON networks.
 */
export const ICON_NETWORK_PREFIX = 'ICON';

/** Stand-in end date for an open-ended instruction, matching `isCreativeLive`'s own default. */
export const OPEN_ENDED = '2100-01-01';

/**
 * The scope of one blanket instruction. Not user-selectable: an instruction exists per
 * network/avail/buy-type combination that the buy actually contains, so these are read off the
 * grid rather than picked from dropdowns.
 */
export interface BlanketCombo {
  network: string;
  avail: (typeof AVAILS)[number];
  type: (typeof TYPES)[number];
}

/** What an instruction's scope is matched against: where and when, but not what length. */
export interface ScopeRow {
  network: string;
  avail: (typeof AVAILS)[number];
  type: (typeof TYPES)[number];
  week: string;
  /** `null` on rows that name no market. */
  market?: string | null;
}

/** A buy row, as far as blanket coverage is concerned. */
export interface CoverageRow extends ScopeRow {
  length: (typeof LENGTHS)[number];
}

/** An instruction's scope: whose rows it applies to, and when. */
export interface InstructionScope extends BlanketCombo {
  start_week: string;
  end_week: string | null;
  market?: string | null;
}

/** The window and notes — the only parts of an instruction the user types. */
export interface DraftDetails {
  startWeek: string;
  endWeek: string;
  notes: string;
}

export const AVAIL_OPTIONS = AVAILS.map(value => ({
  label: value === 'L' ? 'Local' : 'National',
  value
}));

/** A row in one of the blanket editor's per-length rotation sections. */
export interface BlanketRotationRow {
  isci: string;
  creative: string;
  percent: number;
  color: string;
  /** The section's spot length — which tab the row is rendered under. */
  length: (typeof LENGTHS)[number];
  /**
   * The length the save payload scopes this ISCI to. `null` means every length, which is how the
   * rotation already stored it; such a row appears in every section, so this is what keeps it from
   * being rewritten as one concrete-length entry per section.
   */
  savedLength: (typeof LENGTHS)[number] | null;
  /** The creative isn't live for any part of the instruction's window — flagged, not blocked. */
  notLive: boolean;
}

/** Whether the rotation is in a state that can be saved. */
export interface BlanketValidity {
  isValidTotal: boolean;
  hasAllocations: boolean;
  isValid: boolean;
  /** How far off 100% the rotation is, for the error message. */
  offBy: number;
}

/**
 * Restricts the native date inputs to Mondays. `step` on `type='date'` counts days from `min`, so
 * anchoring `min` to a Monday and stepping by 7 greys out every other day in the browser's own
 * calendar. Paired with snapping on change, which is what actually guarantees a Monday: `step`
 * validation isn't uniformly enforced across browsers, and a typed-in date bypasses the calendar.
 */
const MONDAY_EPOCH = '2024-01-01';
export const MONDAYS_ONLY = { min: MONDAY_EPOCH, step: 7 } as const;
